import { useEffect } from "react";
import { useLocation } from "react-router-dom";
//components
import Button from "../components/Button";
import Header from "../components/Header";
import ScrollOnNavigation from "../components/ScrollOnNavigation";
//styles
import {
  StyledSection,
  StyledContainer,
  StyledSectionTitle,
  StyledIntro,
  StyledStepTitle,
  StyledText,
  StyledList,
  StyledNote,
  StyledOptions,
  StyledPackages,
  StyledPackageCard,
  StyledMiniPackages,
  StyledInfoCard,
  StyledPrice,
  StyledFinePrint,
  StyledCta,
  StyledBookRow,
} from "../styles/pages/services";
//animations
import { motion } from "framer-motion";
import { PagesAnimation } from "../styles/Animations";
//constants
import { openCalendly } from "../utils/analytics";
//languages
import { useLanguage } from "../hooks/useLanguage";
import type { LanguageProps } from "../typings";

// Prices read as "<label> — <amount>". Split so the złoty figure can be pulled
// to the right and coloured. Falls back to rendering the whole string when
// there's no dash (e.g. an untranslated Polish placeholder).
const PriceRow = ({ price }: { price: string }) => {
  const [label, amount] = price.split("—");

  if (amount === undefined) {
    return <li>{price}</li>;
  }

  return (
    <li>
      <span>{label.trim()}</span>
      <span className="amount">{amount.trim()}</span>
    </li>
  );
};

const Services = ({ language }: LanguageProps) => {
  const lang = useLanguage(language);
  const s = lang.services;
  const location = useLocation();

  const scrollToId = (id: string) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Nav links point at /services#residence, #citizenship, #transcription and
  // #other. React Router v5 doesn't scroll to the hash on its own, so we do it
  // here — both when arriving from another page and when only the hash changes
  // while already on /services. Mirrors the pattern used on the FAQ page.
  useEffect(() => {
    const scrollToHashSection = () => {
      const id = window.location.hash.slice(1);
      if (!id) {
        return;
      }
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const timer = setTimeout(scrollToHashSection, 100);
    window.addEventListener("hashchange", scrollToHashSection);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("hashchange", scrollToHashSection);
    };
  }, [location]);

  const res = s.residence;
  const cit = s.citizenship;
  const tr = s.transcription;

  return (
    <motion.div
      variants={PagesAnimation}
      initial="hidden"
      animate="show"
      exit="exit"
    >
      <Header language={language} />

      {/* ============ SECTION 1: KARTA POBYTU (lightBg band) ============ */}
      <StyledSection id="residence" $alt={false}>
        <StyledContainer>
          <StyledSectionTitle>{res.title}</StyledSectionTitle>
          <StyledIntro>
            <p>{res.intro}</p>
          </StyledIntro>
          <StyledList>
            {res.cards.map((card, idx) => (
              <li key={idx}>{card}</li>
            ))}
          </StyledList>
          {res.disclaimer.map((p, idx) => (
            <StyledText key={idx}>{p}</StyledText>
          ))}

          {/* Step 1 — situation review */}
          <StyledStepTitle>{res.step1.title}</StyledStepTitle>
          <StyledText>{res.step1.text}</StyledText>
          <StyledOptions>
            {res.step1.options.map((opt, idx) => {
              const [label, amount] = opt.split("—");

              return (
                <>
                  <li key={idx}>
                    {label} - <span className="amount">{amount.trim()}</span>
                  </li>
                </>
              );
            })}
          </StyledOptions>
          <StyledNote>
            <p>
              {res.step1.important[0]} (
              <span className="amount">{res.step1.important[1]}</span>){" "}
              {res.step1.important[2]}
            </p>
          </StyledNote>

          {/* Step 2 — choose a package (the two cards side by side) */}
          <StyledStepTitle>{res.step2.title}</StyledStepTitle>
          <StyledText>{res.step2.intro}</StyledText>
          <StyledPackages>
            {res.step2.packages.map((pkg, idx) => (
              <StyledPackageCard key={idx} $featured={idx === 1}>
                <p className="package-name">
                  <span>{pkg.badge}</span>
                  {pkg.name}
                </p>
                <p className="package-desc">{pkg.description}</p>
                <StyledList>
                  {pkg.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </StyledList>
                {pkg.note ? (
                  <StyledNote>
                    <p>{pkg.note}</p>
                  </StyledNote>
                ) : null}
                <div className="prices">
                  <p className="prices-label">{pkg.pricesLabel}</p>
                  <ul>
                    {pkg.prices.map((price, i) => (
                      <PriceRow key={i} price={price} />
                    ))}
                  </ul>
                </div>
                {pkg.discount ? (
                  <p className="discount">{pkg.discount}</p>
                ) : null}
              </StyledPackageCard>
            ))}
          </StyledPackages>

          {/* Additional services */}
          <StyledStepTitle>{res.additional.title}</StyledStepTitle>
          <StyledText>{res.additional.intro}</StyledText>
          {res.additional.items.map((item, idx) => (
            <StyledInfoCard key={idx}>
              <p className="card-title">{item.title}</p>
              <StyledText>{item.text}</StyledText>
              <p className="card-price">{item.price}</p>
            </StyledInfoCard>
          ))}

          <StyledBookRow>
            <Button
              value={res.button}
              size="small"
              onClick={() => openCalendly("service_residence")}
            />
          </StyledBookRow>
          <StyledFinePrint>
            {res.footerNote.map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </StyledFinePrint>
        </StyledContainer>
      </StyledSection>

      {/* ============ SECTION 2: ГРАЖДАНСТВО (white band) ============ */}
      <StyledSection id="citizenship" $alt={true}>
        <StyledContainer>
          <StyledSectionTitle>{cit.title}</StyledSectionTitle>
          <StyledIntro>
            {cit.intro.map((p, idx) => (
              <p key={idx}>{p}</p>
            ))}
          </StyledIntro>
          <StyledList>
            {cit.services.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </StyledList>

          {/* Step 1 — transcription prerequisite, links down to section 3 */}
          <StyledStepTitle>{cit.step1.title}</StyledStepTitle>
          <StyledText>{cit.step1.text}</StyledText>
          <StyledCta type="button" onClick={() => scrollToId("transcription")}>
            {cit.step1.link}
          </StyledCta>

          {/* Step 2 — two routes to citizenship */}
          <StyledStepTitle>{cit.step2.title}</StyledStepTitle>
          <StyledText>{cit.step2.intro}</StyledText>

          <StyledInfoCard>
            <p className="card-title">{cit.step2.variant1.title}</p>
            {cit.step2.variant1.text.map((p, idx) => (
              <StyledText key={idx}>{p}</StyledText>
            ))}
            <StyledMiniPackages>
              {cit.step2.variant1.packages.map((pkg, idx) => (
                <div
                  className={idx === 1 ? "mini-card featured" : "mini-card"}
                  key={idx}
                >
                  <p className="mini-name">
                    <span>{idx === 1 ? "🔸" : "🔹"}</span>
                    {pkg.name}
                  </p>
                  <p className="mini-text">{pkg.text}</p>
                </div>
              ))}
            </StyledMiniPackages>
          </StyledInfoCard>

          <StyledInfoCard>
            <p className="card-title">{cit.step2.variant2.title}</p>
            {cit.step2.variant2.text.map((p, idx) => (
              <StyledText key={idx}>{p}</StyledText>
            ))}
            <StyledList>
              {cit.step2.variant2.points.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </StyledList>
            <StyledPrice>
              {cit.step2.variant2.price.split("—")[0]} -
              <span className="amount">
                {cit.step2.variant2.price.split("—")[1]}
              </span>
            </StyledPrice>
          </StyledInfoCard>

          <StyledBookRow>
            <Button
              value={cit.button}
              size="small"
              onClick={() => openCalendly("service_citizenship")}
            />
          </StyledBookRow>
          <StyledFinePrint>
            {cit.footerNote.map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </StyledFinePrint>
        </StyledContainer>
      </StyledSection>

      {/* ============ SECTION 3: ТРАНСКРИПЦИЯ (lightBg band) ============ */}
      <StyledSection id="transcription" $alt={false}>
        <StyledContainer>
          <StyledSectionTitle>{tr.title}</StyledSectionTitle>
          <StyledIntro>
            <p>{tr.intro}</p>
          </StyledIntro>
          <StyledText>{tr.text}</StyledText>
          <StyledText>{tr.servicesIntro}</StyledText>
          <StyledList>
            {tr.services.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </StyledList>
          <StyledPrice>
            {tr.price[0]} <span className="amount">{tr.price[1]}</span>{" "}
            {tr.price[2]}
          </StyledPrice>

          <StyledBookRow>
            <Button
              value={tr.additional.button}
              size="small"
              onClick={() => openCalendly("service_transcription")}
            />
          </StyledBookRow>
        </StyledContainer>
      </StyledSection>

      {/* ============ SECTION 4: ADDITIONAL SERVICES (white band) ============ */}
      <StyledSection id="other" $alt={true}>
        <StyledContainer>
          <StyledSectionTitle>{tr.additional.title}</StyledSectionTitle>
          <StyledText>{tr.additional.intro}</StyledText>

          <StyledInfoCard>
            <StyledStepTitle>{tr.additional.basicTitle}</StyledStepTitle>
            <StyledText>
              {tr.additional.basicSubtitle[0]}
              <span className="amount">{tr.additional.basicSubtitle[1]}</span>
              {tr.additional.basicSubtitle[2]}
            </StyledText>
            <StyledList>
              {tr.additional.basicServices.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </StyledList>
            <StyledNote>
              <p>{tr.additional.basicNote}</p>
            </StyledNote>
          </StyledInfoCard>

          <StyledInfoCard>
            <StyledStepTitle>{tr.additional.extraTitle}</StyledStepTitle>
            <StyledList>
              {tr.additional.extraServices.map((item, idx) => {
                const [label, amount] = item.split("—");
                const price = amount.split("/")[0].trim();
                const posfix = amount.split("/")[1].trim();

                return (
                  <li key={idx}>
                    {label.trim()} - <span className="amount">{price}</span> /{" "}
                    {posfix}
                  </li>
                );
              })}
            </StyledList>
          </StyledInfoCard>

          <StyledInfoCard>
            <StyledStepTitle>{tr.additional.complexTitle}</StyledStepTitle>
            <StyledList>
              {tr.additional.complexServices.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </StyledList>
            <StyledFinePrint style={{ marginTop: "1rem", textAlign: "left" }}>
              {tr.additional.complexNote.map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </StyledFinePrint>
          </StyledInfoCard>

          <StyledNote>
            {tr.additional.importantNote.map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </StyledNote>

          <StyledBookRow>
            <Button
              value={tr.additional.button}
              size="small"
              onClick={() => openCalendly("service_other")}
            />
          </StyledBookRow>
        </StyledContainer>
      </StyledSection>

      <ScrollOnNavigation />
    </motion.div>
  );
};

export default Services;
